// Copyright (c) 2021 WiseTime. All rights reserved.

import { Observable, asyncScheduler as scheduler } from "rxjs"
import { filter, map, scan } from "rxjs/operators"

/**
 * Throttles identical values (compared by equal function) from the source for duration milliseconds.
 * If values from subsequent source differ they will not be throttled.
 *
 * @param duration - Time to wait before emitting another value if not distinct to the previous one.
 * @param equals - Function of distinct computation. Reference comparison by default.
 */
export function throttleSame<T>(
  duration: number,
  equals: (a: T, b: T) => boolean = (a, b) => a === b,
) {
  return (source: Observable<T>): Observable<T> => {
    return source
      .pipe(
        map((x) => ({ val: x, time: scheduler.now(), keep: true })),
        scan((acc, cur) => {
          const diff = cur.time - acc.time
          const isSame = equals(acc.val, cur.val)
          return diff > duration || !isSame
            ? { ...cur, keep: true }
            : { ...acc, keep: false }
        }),
        filter((x) => x.keep),
        map((x) => x.val),
      )
  }
}
